import { useId, useState, type ReactNode } from 'react';
import { ChevronDown, Plus, Trash2 } from 'lucide-react';
import { Modale } from '../ui/Modale';
import { BoutonPrincipal, BoutonSecondaire, MarqueObligatoire, MessageErreur } from '../ui/Primitives';
import { useToast } from '../ui/useToast';
import { ErreurApi } from '../../lib/api';
import { FICHE_VIERGE, useCreerSuiviAccompagnement, type FicheAccompagnement } from '../../lib/useAccompagnement';

const CLASSE_CHAMP =
  'min-h-[44px] w-full rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm text-codialis-navy focus:border-codialis-sky focus:outline-none focus:ring-2 focus:ring-codialis-sky/20';

function aujourdhui(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Indian/Reunion' }).format(new Date());
}

/** `prioritaire` : la problématique et le plan d'action, mis en avant (TF-470). */
function Rubrique({
  titre,
  prioritaire = false,
  obligatoire = false,
  children,
}: {
  titre: string;
  prioritaire?: boolean;
  obligatoire?: boolean;
  children: ReactNode;
}): JSX.Element {
  return (
    <fieldset
      className={`min-w-0 space-y-3 rounded-2xl border p-4 ${
        prioritaire ? 'border-codialis-sky/40 bg-codialis-sky/5' : 'border-gray-200'
      }`}
    >
      <legend className="px-1 text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
        {titre}
        {obligatoire && <MarqueObligatoire />}
      </legend>
      {children}
    </fieldset>
  );
}

function Case({
  libelle,
  coche,
  onChanger,
}: {
  libelle: string;
  coche: boolean;
  onChanger: (coche: boolean) => void;
}): JSX.Element {
  return (
    <label className="flex min-h-[40px] items-center gap-2 text-sm text-codialis-navy">
      <input
        type="checkbox"
        checked={coche}
        onChange={(e) => onChanger(e.target.checked)}
        className="h-5 w-5 shrink-0"
      />
      {libelle}
    </label>
  );
}

function Texte({
  libelle,
  valeur,
  onChanger,
  lignes = 1,
  obligatoire = false,
}: {
  libelle: string;
  valeur: string;
  onChanger: (valeur: string) => void;
  lignes?: number;
  obligatoire?: boolean;
}): JSX.Element {
  return (
    <label className="block min-w-0">
      <span className="mb-1 block text-xs text-codialis-gray">
        {libelle}
        {obligatoire && <MarqueObligatoire />}
      </span>
      {lignes > 1 ? (
        <textarea
          value={valeur}
          onChange={(e) => onChanger(e.target.value)}
          rows={lignes}
          maxLength={2000}
          required={obligatoire}
          className={`${CLASSE_CHAMP} py-2`}
        />
      ) : (
        <input value={valeur} onChange={(e) => onChanger(e.target.value)} maxLength={2000} className={CLASSE_CHAMP} />
      )}
    </label>
  );
}

interface LigneAction {
  libelle: string;
  echeance: string;
  responsable: string;
}

/**
 * Entretien d'accompagnement, sur les rubriques du modèle client « Fiche de
 * suivi d'entretien ». L'apprenant est déjà en formation (TF-470) : la
 * problématique et le plan d'action passent en tête et sont seuls exigés ;
 * objectif, démarches réalisées et accompagnement prévu, tournés vers la
 * recherche d'entreprise, sont repliés en compléments facultatifs. Le plan
 * d'action est la liste des actions engagées, suivies ensuite une à une. À
 * l'enregistrement, la fiche est produite et attend la signature de
 * l'apprenant et du conseiller.
 */
export function ModaleEntretienAccompagnement({
  apprenants,
  onFermer,
}: {
  apprenants: { id: string; nom: string; siteCode: string }[];
  onFermer: () => void;
}): JSX.Element {
  const notifier = useToast();
  const creer = useCreerSuiviAccompagnement();
  const [apprenantId, setApprenantId] = useState('');
  const [dateEntretien, setDateEntretien] = useState(aujourdhui());
  const [fiche, setFiche] = useState<FicheAccompagnement>(FICHE_VIERGE);
  const [actions, setActions] = useState<LigneAction[]>([{ libelle: '', echeance: '', responsable: '' }]);
  const [compteRendu, setCompteRendu] = useState('');
  const [complements, setComplements] = useState(false);
  const idComplements = useId();

  const majFiche = <K extends keyof FicheAccompagnement>(
    cle: K,
    valeur: Partial<FicheAccompagnement[K]> | string,
  ): void =>
    setFiche((courante) => ({
      ...courante,
      [cle]: typeof valeur === 'string' ? valeur : { ...(courante[cle] as object), ...valeur },
    }));

  const actionsSaisies = actions.filter((action) => action.libelle.trim());
  // Même exigence que l'API : difficultés décrites et au moins une action.
  const complete =
    Boolean(apprenantId) &&
    Boolean(dateEntretien) &&
    fiche.difficultesRencontrees.trim().length >= 2 &&
    actionsSaisies.length > 0;

  return (
    <Modale
      titre="Entretien d’accompagnement"
      sousTitre="Fiche de suivi d’entretien · à faire signer par l’apprenant et vous"
      ouverte
      onFermer={onFermer}
      largeur="max-w-4xl"
    >
      <div className="space-y-4 p-4 sm:p-6">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_12rem]">
          <label className="block min-w-0">
            <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
              Apprenant
            </span>
            <select value={apprenantId} onChange={(e) => setApprenantId(e.target.value)} className={CLASSE_CHAMP}>
              <option value="">Choisir un apprenant…</option>
              {apprenants.map((apprenant) => (
                <option key={apprenant.id} value={apprenant.id}>
                  {apprenant.nom} · {apprenant.siteCode}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
              Date de l’entretien
            </span>
            <input
              type="date"
              value={dateEntretien}
              onChange={(e) => setDateEntretien(e.target.value)}
              className={CLASSE_CHAMP}
            />
          </label>
        </div>

        <Rubrique titre="Situation actuelle" prioritaire>
          <Texte
            libelle="Difficultés rencontrées · la problématique de l’apprenant"
            valeur={fiche.difficultesRencontrees}
            onChanger={(v) => majFiche('difficultesRencontrees', v)}
            lignes={4}
            obligatoire
          />
          <Texte
            libelle="Points positifs"
            valeur={fiche.pointsPositifs}
            onChanger={(v) => majFiche('pointsPositifs', v)}
            lignes={2}
          />
        </Rubrique>

        <Rubrique titre="Difficultés identifiées">
          <div className="grid grid-cols-1 sm:grid-cols-3">
            <Case
              libelle="Motivation"
              coche={fiche.difficultesIdentifiees.motivation}
              onChanger={(v) => majFiche('difficultesIdentifiees', { motivation: v })}
            />
            <Case
              libelle="Confiance"
              coche={fiche.difficultesIdentifiees.confiance}
              onChanger={(v) => majFiche('difficultesIdentifiees', { confiance: v })}
            />
            <Case
              libelle="Organisation"
              coche={fiche.difficultesIdentifiees.organisation}
              onChanger={(v) => majFiche('difficultesIdentifiees', { organisation: v })}
            />
            <Case
              libelle="Communication"
              coche={fiche.difficultesIdentifiees.communication}
              onChanger={(v) => majFiche('difficultesIdentifiees', { communication: v })}
            />
            <Case
              libelle="Problèmes personnels"
              coche={fiche.difficultesIdentifiees.problemesPersonnels}
              onChanger={(v) => majFiche('difficultesIdentifiees', { problemesPersonnels: v })}
            />
          </div>
          <Texte
            libelle="Autre"
            valeur={fiche.difficultesIdentifiees.autre}
            onChanger={(v) => majFiche('difficultesIdentifiees', { autre: v })}
          />
        </Rubrique>

        <Rubrique titre="Solutions proposées">
          <Texte
            libelle="Solutions"
            valeur={fiche.solutionsProposees}
            onChanger={(v) => majFiche('solutionsProposees', v)}
            lignes={2}
          />
        </Rubrique>

        <Rubrique titre="Plan d’action · démarches à réaliser" prioritaire obligatoire>
          <p className="text-[11px] text-codialis-gray">
            Chaque démarche devient une action suivie dans le tableau des actions, jusqu’à sa réalisation.
          </p>
          {actions.map((action, rang) => (
            <div key={rang} className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_10rem_12rem_auto]">
              <input
                value={action.libelle}
                onChange={(e) =>
                  setActions((liste) => liste.map((l, i) => (i === rang ? { ...l, libelle: e.target.value } : l)))
                }
                placeholder={`Démarche ${rang + 1}`}
                maxLength={300}
                aria-label={`Démarche ${rang + 1}`}
                className={CLASSE_CHAMP}
              />
              <input
                type="date"
                value={action.echeance}
                onChange={(e) =>
                  setActions((liste) => liste.map((l, i) => (i === rang ? { ...l, echeance: e.target.value } : l)))
                }
                aria-label={`Échéance de la démarche ${rang + 1}`}
                className={CLASSE_CHAMP}
              />
              <input
                value={action.responsable}
                onChange={(e) =>
                  setActions((liste) => liste.map((l, i) => (i === rang ? { ...l, responsable: e.target.value } : l)))
                }
                placeholder="Qui la porte"
                maxLength={120}
                aria-label={`Responsable de la démarche ${rang + 1}`}
                className={CLASSE_CHAMP}
              />
              <button
                type="button"
                onClick={() => setActions((liste) => liste.filter((_, i) => i !== rang))}
                disabled={actions.length === 1}
                aria-label={`Retirer la démarche ${rang + 1}`}
                className="inline-flex h-11 w-11 items-center justify-center rounded-lg text-codialis-gray transition hover:bg-gray-100 disabled:opacity-30"
              >
                <Trash2 className="h-4 w-4" aria-hidden />
              </button>
            </div>
          ))}
          {actions.length < 20 && (
            <button
              type="button"
              onClick={() => setActions((liste) => [...liste, { libelle: '', echeance: '', responsable: '' }])}
              className="inline-flex min-h-[40px] items-center gap-1.5 text-xs font-semibold text-codialis-sky hover:underline"
            >
              <Plus className="h-3.5 w-3.5" aria-hidden />
              Ajouter une démarche
            </button>
          )}
        </Rubrique>

        <Rubrique titre="Prochain rendez-vous">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[12rem_1fr]">
            <label className="block">
              <span className="mb-1 block text-xs text-codialis-gray">Date</span>
              <input
                type="date"
                value={fiche.prochainRendezVous.date}
                onChange={(e) => majFiche('prochainRendezVous', { date: e.target.value })}
                className={CLASSE_CHAMP}
              />
            </label>
            <Texte
              libelle="Objectif"
              valeur={fiche.prochainRendezVous.objectif}
              onChanger={(v) => majFiche('prochainRendezVous', { objectif: v })}
            />
          </div>
        </Rubrique>

        <div className="min-w-0 rounded-2xl border border-gray-200">
          <button
            type="button"
            onClick={() => setComplements((ouvert) => !ouvert)}
            aria-expanded={complements}
            aria-controls={idComplements}
            className="flex min-h-[44px] w-full items-center gap-2 px-4 py-3 text-left"
          >
            <span className="min-w-0 flex-1">
              <span className="block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">
                Compléments facultatifs
              </span>
              <span className="block text-[11px] text-codialis-gray">
                Objectif de l’entretien, projet professionnel, démarches réalisées, recherche d’entreprise
              </span>
            </span>
            <ChevronDown
              className={`h-4 w-4 shrink-0 text-codialis-gray transition ${complements ? 'rotate-180' : ''}`}
              aria-hidden
            />
          </button>
          {complements && (
            <div id={idComplements} className="space-y-4 border-t border-gray-100 p-4">
              <Rubrique titre="Objectif de l’entretien">
                <div className="grid grid-cols-1 sm:grid-cols-2">
                  <Case
                    libelle="Suivi régulier"
                    coche={fiche.objectif.suiviRegulier}
                    onChanger={(v) => majFiche('objectif', { suiviRegulier: v })}
                  />
                  <Case
                    libelle="Point sur la situation"
                    coche={fiche.objectif.pointSituation}
                    onChanger={(v) => majFiche('objectif', { pointSituation: v })}
                  />
                  <Case
                    libelle="Difficulté rencontrée"
                    coche={fiche.objectif.difficulteRencontree}
                    onChanger={(v) => majFiche('objectif', { difficulteRencontree: v })}
                  />
                  <Case
                    libelle="Projet professionnel"
                    coche={fiche.objectif.projetProfessionnel}
                    onChanger={(v) => majFiche('objectif', { projetProfessionnel: v })}
                  />
                </div>
                <Texte
                  libelle="Autre"
                  valeur={fiche.objectif.autre}
                  onChanger={(v) => majFiche('objectif', { autre: v })}
                />
              </Rubrique>

              <Rubrique titre="Démarches réalisées">
                <div className="grid grid-cols-1 sm:grid-cols-2">
                  <Case
                    libelle="Recherche de stage / formation"
                    coche={fiche.demarches.rechercheStageFormation}
                    onChanger={(v) => majFiche('demarches', { rechercheStageFormation: v })}
                  />
                  <Case
                    libelle="Candidatures envoyées"
                    coche={fiche.demarches.candidaturesEnvoyees}
                    onChanger={(v) => majFiche('demarches', { candidaturesEnvoyees: v })}
                  />
                  <Case
                    libelle="Prises de contact"
                    coche={fiche.demarches.prisesContact}
                    onChanger={(v) => majFiche('demarches', { prisesContact: v })}
                  />
                  <Case
                    libelle="Rendez-vous (structures, partenaires…)"
                    coche={fiche.demarches.rendezVous}
                    onChanger={(v) => majFiche('demarches', { rendezVous: v })}
                  />
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Texte
                    libelle="Autre"
                    valeur={fiche.demarches.autre}
                    onChanger={(v) => majFiche('demarches', { autre: v })}
                  />
                  <Texte
                    libelle="Détail"
                    valeur={fiche.demarches.detail}
                    onChanger={(v) => majFiche('demarches', { detail: v })}
                  />
                </div>
              </Rubrique>

              <Rubrique titre="Accompagnement prévu">
                <Case
                  libelle="Aide CV"
                  coche={fiche.accompagnement.aideCv}
                  onChanger={(v) => majFiche('accompagnement', { aideCv: v })}
                />
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Texte
                    libelle="Mise en relation"
                    valeur={fiche.accompagnement.miseEnRelation}
                    onChanger={(v) => majFiche('accompagnement', { miseEnRelation: v })}
                  />
                  <Texte
                    libelle="Autre"
                    valeur={fiche.accompagnement.autre}
                    onChanger={(v) => majFiche('accompagnement', { autre: v })}
                  />
                </div>
              </Rubrique>
            </div>
          )}
        </div>

        <Texte
          libelle="Compte rendu interne (facultatif, n’apparaît pas sur la fiche)"
          valeur={compteRendu}
          onChanger={setCompteRendu}
          lignes={2}
        />

        {creer.error instanceof ErreurApi && <MessageErreur message={creer.error.message} />}

        <div className="flex flex-wrap gap-2 border-t border-gray-100 pt-4">
          <BoutonPrincipal
            onClick={() =>
              creer.mutate(
                {
                  apprenantId,
                  dateEntretien,
                  fiche,
                  actions: actionsSaisies.map((action) => ({
                    libelle: action.libelle.trim(),
                    ...(action.echeance ? { echeance: action.echeance } : {}),
                    ...(action.responsable.trim() ? { responsable: action.responsable.trim() } : {}),
                  })),
                  ...(compteRendu.trim() ? { compteRendu: compteRendu.trim() } : {}),
                },
                {
                  onSuccess: ({ message }) => {
                    notifier(message);
                    onFermer();
                  },
                },
              )
            }
            disabled={creer.isPending || !complete}
          >
            {creer.isPending ? 'Enregistrement…' : 'Enregistrer et produire la fiche'}
          </BoutonPrincipal>
          <BoutonSecondaire onClick={onFermer} disabled={creer.isPending}>
            Annuler
          </BoutonSecondaire>
        </div>
      </div>
    </Modale>
  );
}
